import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePagination, useSortBy, useTable } from "react-table";
import { Button, Col, Input, Row, Table } from "reactstrap";
import { selectUserRole } from "redux/user/selectors";
import { getColumns } from "./GrowthPlanAssessmentColumn";
import { selectUserToken } from "redux/user/selectors";
import { fetchGrowthPlanAssessmentListThunkAction } from "redux/growPlanAssessment/actions";
import { selectGrowthPlan } from "redux/growPlanAssessment/selectors";
import Loader from "components/Loader/Loader";
import Paginate from "components/Pagination/Pagination";
import confirmationPopup from "utils/confirmationPopup";
import { markAsVerifiedGrowthPlanAssessmentThunkAction } from "redux/growPlanAssessment/actions";
import { fetchDownloadGrowthPlanReportThunkAction } from "redux/growPlanAssessment/actions";
import toast from "react-hot-toast";
import BackdropLoader from "components/BackdropLoader/BackdropLoader";

const INITIAL_PAGE_INDEX = 0;
const INITIAL_PAGE_SIZE = 10;

function GrowthPlanAssessment() {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const role = useSelector(selectUserRole) || localStorage.getItem("role");
  const token = useSelector(selectUserToken);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    isGrowthPlanAssessmentListLoading,
    growthPlanAssessmentListError,
    growthPlanAssessmentList,
    totalPage
  } = useSelector(selectGrowthPlan);

  const [index, setIndex] = useState();
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(null);

  const markAsVerifiedHandler = React.useCallback(
    async (id) => {
      try {
        const modalContent = `<h4 style="font-size:1.2rem;">Are you sure you want to mark <strong>Assessment No. ${id}</strong> as verified ?</h4>`;
        const result = await confirmationPopup(modalContent);

        if (result.isConfirmed) {
          dispatch(markAsVerifiedGrowthPlanAssessmentThunkAction(id));
        }
      } catch (error) {
        console.log(error);
      }
    },
    [growthPlanAssessmentList, role, token]
  );

  const downloadHandler = React.useCallback(
    (id) => {
      setIsDownloading(true);
      toast.loading("Fetching Growth Plan Report...");
      dispatch(
        fetchDownloadGrowthPlanReportThunkAction(
          id,
          () => {
            toast.dismiss();
            setIsDownloading(false);
          },
          () => {
            toast.dismiss();
            setIsDownloading(false);
            toast.error("Failed to fetch growth plan report.");
          }
        )
      );
    },
    [dispatch]
  );

  const columns = useMemo(
    () => getColumns(markAsVerifiedHandler, downloadHandler),
    [markAsVerifiedHandler, downloadHandler]
  );
  const data = useMemo(() => growthPlanAssessmentList, [growthPlanAssessmentList]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: INITIAL_PAGE_INDEX, sortBy: [], pageSize: INITIAL_PAGE_SIZE },
      manualPagination: true,
      manualSortBy: true,
      disableMultiSort: false,
      manualGlobalFilter: true,
      pageCount: Math.ceil(totalPage)
    },
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    pageOptions,
    pageCount,
    gotoPage,
    state,
    setSortBy,
    setPageSize
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  const onSuccess = () => {
    setError(null);
  };

  const onError = (error) => {
    setError(error);
  };

  useEffect(() => {
    if (token) {
      dispatch(
        fetchGrowthPlanAssessmentListThunkAction(
          {
            limit: pageSize,
            offset: pageIndex,
            column: sort?.sortBy,
            sort: sort?.sortDirection
          },
          onSuccess,
          onError
        )
      );
    }
  }, [pageIndex, pageSize, globalFilter, token, sort]);

  useEffect(() => {
    const getIndex = () => {
      return Number(`${pageIndex * pageSize}`);
    };
    setIndex(getIndex);
  }, [pageIndex, pageSize]);

  return (
    <div className="px-3">
      {isDownloading ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9999
          }}
        >
          <BackdropLoader />
        </div>
      ) : null}
      <div className="d-flex justify-content-between my-2">
        <h4>Growth Plan Assessment</h4>
        {+role === 4 || +role === 3 ? (
          <div>
            <Button color="primary" className="px-3 py-2" onClick={() => navigate("add")}>
              Start Assessment
            </Button>
          </div>
        ) : null}
      </div>

      <div className="py-2">
        <nav aria-label="..." className="post_footer">
          <Row className="pagination justify-content-end mb-0 d-flex flex-wrap align-items-center">
            <Col xs="12" md="12" className="d-flex align-items-center">
              <span>Show</span>
              <Input
                type="select"
                style={{
                  width: "7rem",
                  margin: "10px",
                  cursor: "pointer"
                }}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[5, 10, 20, 30, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
        </nav>
      </div>

      <Row className="mt-2">
        <Col
          style={{
            minHeight: "65vh"
          }}
        >
          {isGrowthPlanAssessmentListLoading ? (
            <Fragment>
              <div className="post_error_loading">
                <Loader />
              </div>
            </Fragment>
          ) : (
            <Fragment>
              {error ? (
                <Fragment>
                  <div className="post_error_loading">{error}</div>
                </Fragment>
              ) : (
                <Fragment>
                  <Table
                    {...getTableProps()}
                    className="align-items-center table-flush governmentlistofsurvey_table_container"
                    responsive
                    bordered
                  >
                    <thead className="thead-light governmentlistofsurvey_table_header">
                      {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup}>
                          {headerGroup.headers.map((column) => (
                            <th
                              key={column.id}
                              onClick={() => {
                                if (!column.disableSortBy) {
                                  const desc =
                                    column.isSortedDesc === true
                                      ? undefined
                                      : column.isSortedDesc === false
                                      ? true
                                      : false;
                                  setSort({
                                    sortBy: column.id,
                                    sortDirection: desc ? "desc" : "asc"
                                  });
                                  setSortBy([{ id: column.id, desc }]);
                                }
                              }}
                              {...column.getHeaderProps()}
                            >
                              {column.render("Header")}
                              <span>
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <i className="fas fa-arrow-down ml-2"></i>
                                  ) : (
                                    <i className="fas fa-arrow-up ml-2"></i>
                                  )
                                ) : (
                                  ""
                                )}
                              </span>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr {...row.getRowProps()} key={row.id}>
                            {row.cells.map((cell) => {
                              return (
                                <td key={cell.id} {...cell.getCellProps()}>
                                  {cell.render("Cell")}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  {!error && growthPlanAssessmentList?.length === 0 && (
                    <div className="post_error_loading pt-3">No growth plan assessment found.</div>
                  )}
                </Fragment>
              )}
            </Fragment>
          )}
        </Col>

        <Col xs="12" className="d-flex justify-content-end align-items-center">
          <nav aria-label="..." className="post_footer">
            <Paginate
              pageIndex={pageIndex}
              pageCount={pageCount}
              previousPage={previousPage}
              nextPage={nextPage}
              gotoPage={gotoPage}
            />
          </nav>
        </Col>
      </Row>
    </div>
  );
}

export default GrowthPlanAssessment;
